import type { ContentBlock } from '../types/api'

export type ShapeType = 'circle' | 'square' | 'rectangle'

export interface OriginalShape {
  type: ShapeType
  diameter?: number
  sideA?: number
  sideB?: number
}

export interface RecipeMeta {
  originalShape: OriginalShape | null
  originalHeight: number | null
  originalWeight: number | null
}

export interface ParsedIngredient {
  name: string
  amount: number | null
  unit: string
}

export interface ParsedSection {
  name: string
  scalable: boolean
  ingredients: ParsedIngredient[]
}

const NON_SCALABLE_SECTIONS = ['для декора', 'декор']

/**
 * Парсит строку ингредиента вида:
 * "Мука — 100 г", "• Яйца – 200 г", "Ванильная паста — по вкусу"
 * "• Фундук обжаренный – 10г" (без пробела перед г)
 */
function parseIngredientText(text: string): ParsedIngredient | null {
  const clean = text
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/^[•\-\s]+/, '')
    .trim()

  if (!clean) return null

  // "Название — число единица"
  const match = clean.match(
    /^(.+?)\s*[—–\-]\s*(\d+(?:[.,]\d+)?)\s*(.*)$/
  )

  if (match) {
    const name = match[1].trim()
    const amount = parseFloat(match[2].replace(',', '.'))
    const unit = match[3].trim() || 'г'
    return { name, amount, unit }
  }

  // "Название — по вкусу"
  const matchNoAmount = clean.match(/^(.+?)\s*[—–\-]\s*(.+)$/)
  if (matchNoAmount) {
    return {
      name: matchNoAmount[1].trim(),
      amount: null,
      unit: matchNoAmount[2].trim(),
    }
  }

  // Нет тире — не ингредиент
  return null
}

/**
 * Парсит мета-данные рецепта из стандартной строки.
 *
 * Поддерживаемые форматы:
 *   *Рецепт рассчитан на торт диаметром 16 см, высотой 10 см.
 *   *Рецепт рассчитан на торт 18×18 см, высотой 8 см.
 *   *Рецепт рассчитан на торт 12×32 см, высотой 8 см.
 *   *Рецепт на один бисквит диаметром 16 см.
 *   * Вес кекса ~ 450 г, диаметр — 16 см.
 */
function parseMetaFromText(text: string, currentMeta: RecipeMeta): RecipeMeta {
  const clean = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')

  const meta = { ...currentMeta }

  // Прямоугольник/квадрат: "12×32 см" или "18x18 см"
  if (!meta.originalShape) {
    const rectMatch = clean.match(
      /(\d+(?:[.,]\d+)?)\s*[×xXхХ]\s*(\d+(?:[.,]\d+)?)\s*см/
    )
    if (rectMatch) {
      const a = parseFloat(rectMatch[1].replace(',', '.'))
      const b = parseFloat(rectMatch[2].replace(',', '.'))
      meta.originalShape =
        a === b
          ? { type: 'square', sideA: a }
          : { type: 'rectangle', sideA: a, sideB: b }
    }
  }

  // Круг: "диаметром 16 см" или "диаметр — 16 см"
  if (!meta.originalShape) {
    const diameterMatch = clean.match(
      /диаметр(?:ом)?\s*[—–\-]?\s*~?\s*(\d+(?:[.,]\d+)?)\s*см/i
    )
    if (diameterMatch) {
      meta.originalShape = {
        type: 'circle',
        diameter: parseFloat(diameterMatch[1].replace(',', '.')),
      }
    }
  }

  // Высота: "высотой 10 см" или "высотой ~ 8 см"
  if (meta.originalHeight === null) {
    const heightMatch = clean.match(
      /высот(?:ой|а)\s*~?\s*(\d+(?:[.,]\d+)?)\s*см/i
    )
    if (heightMatch) {
      meta.originalHeight = parseFloat(heightMatch[1].replace(',', '.'))
    }
  }

  // Вес: "Вес торта: ~ 1500 г", "Вес — 1400г", "Вес: 1400г"
  if (meta.originalWeight === null) {
    const weightMatch = clean.match(
      /вес[^:—–\-]*[:\s—–\-]+\s*~?\s*(\d+(?:[.,]\d+)?)\s*г/i
    )
    if (weightMatch) {
      meta.originalWeight = parseFloat(weightMatch[1].replace(',', '.'))
    }
  }

  return meta
}

/**
 * Извлекает мета-данные (форму, размер, высоту) из content-блоков.
 */
export function parseRecipeMetaFromContent(
  content: ContentBlock[]
): RecipeMeta {
  let meta: RecipeMeta = { originalShape: null, originalHeight: null, originalWeight: null }

  for (const block of content) {
    if (block.type === 'paragraph' && block.data.text) {
      // Парсим мету только из строк, начинающихся с * (мета-строки рецепта)
      const cleanCheck = block.data.text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
      if (cleanCheck.startsWith('*') || /^вес/i.test(cleanCheck)) {
        meta = parseMetaFromText(block.data.text, meta)
      }
    }
    if (meta.originalShape && meta.originalHeight !== null && meta.originalWeight !== null) break
  }

  return meta
}

/**
 * Извлекает секции ингредиентов из content-блоков карточки.
 */
export function parseIngredientsFromContent(
  content: ContentBlock[]
): ParsedSection[] {
  const sections: ParsedSection[] = []
  let inIngredients = false
  let currentSection: ParsedSection | null = null

  for (const block of content) {
    // Начало блока ИНГРЕДИЕНТЫ
    if (
      block.type === 'container' &&
      block.data.text?.toUpperCase().includes('ИНГР')
    ) {
      inIngredients = true
      continue
    }

    // Видео внутри ингредиентов — конец блока (начинается приготовление)
    if (inIngredients && block.type === 'embed') {
      if (currentSection && currentSection.ingredients.length > 0) {
        sections.push(currentSection)
      }
      inIngredients = false
      continue
    }

    // Другой container внутри ингредиентов
    if (inIngredients && block.type === 'container') {
      const text = block.data.text || ''
      const textLetters = text.replace(/[^а-яА-ЯёЁa-zA-Z]/g, '')

      // Контейнер написан КАПСОМ — это раздел верхнего уровня, конец ингредиентов
      const isUpperCase =
        textLetters.length > 2 &&
        textLetters === textLetters.toUpperCase()

      if (isUpperCase) {
        if (currentSection && currentSection.ingredients.length > 0) {
          sections.push(currentSection)
        }
        inIngredients = false
        continue
      }

      // Подсекция ингредиентов (написана не капсом)
      if (currentSection && currentSection.ingredients.length > 0) {
        sections.push(currentSection)
      }
      const sectionName = text || 'Ингредиенты'
      currentSection = {
        name: sectionName,
        scalable: !NON_SCALABLE_SECTIONS.some((s) =>
          sectionName.toLowerCase().includes(s)
        ),
        ingredients: [],
      }
      continue
    }

    if (!inIngredients) continue

    if (!currentSection) {
      currentSection = {
        name: 'Ингредиенты',
        scalable: true,
        ingredients: [],
      }
    }

    if (block.type === 'paragraph' && block.data.text) {
      const cleanText = block.data.text.replace(/<[^>]*>/g, '').trim()
      // Пропускаем мета-строки
      if (
        cleanText.startsWith('*') ||
        cleanText.startsWith('Рецепт') ||
        cleanText.startsWith('Вес')
      )
        continue

      const ingredient = parseIngredientText(block.data.text)
      if (ingredient) {
        currentSection.ingredients.push(ingredient)
      }
    }

    if (block.type === 'list' && block.data.items) {
      for (const item of block.data.items) {
        const ingredient = parseIngredientText(item)
        if (ingredient) {
          currentSection.ingredients.push(ingredient)
        }
      }
    }
  }

  if (currentSection && currentSection.ingredients.length > 0) {
    sections.push(currentSection)
  }

  // Дедупликация секций с одинаковым именем и составом
  const unique: ParsedSection[] = []
  for (const section of sections) {
    const isDuplicate = unique.some(
      (s) =>
        s.name === section.name &&
        s.ingredients.length === section.ingredients.length &&
        s.ingredients.every(
          (ing, i) =>
            ing.name === section.ingredients[i].name &&
            ing.amount === section.ingredients[i].amount
        )
    )
    if (!isDuplicate) {
      unique.push(section)
    }
  }

  return unique
}
